/**
 * CartSummary Component
 * Displays a detailed summary of cart items, promotions, free resins, and packages
 */

import React from 'react';
import { CartItem, Promotion, SelectedFreeResin, SelectedPackage, UIText, Currency } from './types';
import { formatPrice, getEventCurrency, getVatDisplayEnabled, getVatRate, calculateGrossPrice } from './utils';

interface CartSummaryProps {
	cartItems: CartItem[];
	appliedPromotions: Promotion[];
	selectedFreeResins: SelectedFreeResin[];
	selectedPackage?: SelectedPackage | null;
	onRemoveItem?: (itemIndex: number) => void;
	onRemovePromotion?: (promotionId: string) => void;
	onRemoveFreeResin?: (resinUniqueId: string) => void;
	onRemovePackage?: () => void;
	uiText?: UIText['cart'];
}

export const CartSummary: React.FC<CartSummaryProps> = ({
	cartItems,
	appliedPromotions,
	selectedFreeResins,
	selectedPackage,
	onRemoveItem,
	onRemovePromotion,
	onRemoveFreeResin,
	onRemovePackage,
	uiText,
}) => {
	// Calculate line item total
	const calculateLineTotal = (price: number, quantity: number): number => {
		return price * quantity;
	};

	// Get currency from first cart item's product or applied promotion, or default to event currency
	const getCurrency = (): Currency => {
		if (cartItems.length > 0 && cartItems[0].product.currency) {
			return cartItems[0].product.currency;
		}
		if (appliedPromotions.length > 0 && appliedPromotions[0].currency) {
			return appliedPromotions[0].currency;
		}
		return getEventCurrency();
	};

	const currency = getCurrency();

	// Check if cart is empty
	const isEmpty = cartItems.length === 0 && appliedPromotions.length === 0 && selectedFreeResins.length === 0 && !selectedPackage;

	return (
		<div className="sprintry-cart-summary">
			{isEmpty ? (
				<div className="sprintry-cart-summary__empty">
					{uiText?.empty || 'No hay productos en el carrito'}
				</div>
			) : (
				<div className="sprintry-cart-summary__list">
					{/* Cart Items */}
					{cartItems.map((item, index) => (
						<div key={`${item.product.id}-${index}`} className="sprintry-cart-summary__item">
							<div className="sprintry-cart-summary__item-details">
								<div className="sprintry-cart-summary__item-name">
									{item.product.name}
									{item.selectedVariant && (
										<span className="sprintry-cart-summary__item-variant">
											{' '}({item.selectedVariant})
										</span>
									)}
								</div>
								<div className="sprintry-cart-summary__item-meta">
									<span className="sprintry-cart-summary__item-quantity">
										{item.quantity} ×
									</span>
									<span className="sprintry-cart-summary__item-price">
										{formatPrice(
											item.product.price,
											item.product.currency || currency
										)}
									</span>
								</div>
							</div>
							<div className="sprintry-cart-summary__item-total">
								<div className="sprintry-cart-summary__item-amounts">
									<span className="sprintry-cart-summary__item-amount">
										{getVatDisplayEnabled() ? (
											<span>
												{formatPrice(calculateGrossPrice(calculateLineTotal(item.product.price, item.quantity), getVatRate()), item.product.currency || currency)}
												<span className="sprintry-cart-summary__item-amount-label">
													{uiText?.vatIncluded || 'incl. VAT'}
												</span>
											</span>
										) : (
											formatPrice(calculateLineTotal(item.product.price, item.quantity), item.product.currency || currency)
										)}
									</span>
									{getVatDisplayEnabled() && (
										<span className="sprintry-cart-summary__item-vat">
											{formatPrice(calculateLineTotal(item.product.price, item.quantity), item.product.currency || currency)} {uiText?.vatExcluded || 'excl. VAT'}
										</span>
									)}
								</div>
								{onRemoveItem && (
									<button
										type="button"
										className="sprintry-cart-summary__item-remove"
										onClick={() => onRemoveItem(index)}
										aria-label="Remove item"
									>
										×
									</button>
								)}
							</div>
						</div>
					))}

					{/* Applied Promotions */}
					{appliedPromotions.map((promo) => (
						<div key={promo.id} className="sprintry-cart-summary__item sprintry-cart-summary__item--promotion">
							<div className="sprintry-cart-summary__item-details">
								<div className="sprintry-cart-summary__item-name">
									{promo.title}
									<span className="sprintry-cart-summary__item-badge">Promo</span>
								</div>
								<div className="sprintry-cart-summary__item-meta">
									<span className="sprintry-cart-summary__item-quantity">1 ×</span>
									<span className="sprintry-cart-summary__item-price">
										{formatPrice(
											promo.promoPrice,
											promo.currency || currency
										)}
									</span>
								</div>
							</div>
							<div className="sprintry-cart-summary__item-total">
								<div className="sprintry-cart-summary__item-amounts">
									<span className="sprintry-cart-summary__item-amount">
										{getVatDisplayEnabled() ? (
											<span>
												{formatPrice(calculateGrossPrice(promo.promoPrice, getVatRate()), promo.currency || currency)}
												<span className="sprintry-cart-summary__item-amount-label">
													{uiText?.vatIncluded || 'incl. VAT'}
												</span>
											</span>
										) : (
											formatPrice(promo.promoPrice, promo.currency || currency)
										)}
									</span>
									{getVatDisplayEnabled() && (
										<span className="sprintry-cart-summary__item-vat">
											{formatPrice(promo.promoPrice, promo.currency || currency)} {uiText?.vatExcluded || 'excl. VAT'}
										</span>
									)}
								</div>
								{onRemovePromotion && (
									<button
										type="button"
										className="sprintry-cart-summary__item-remove"
										onClick={() => onRemovePromotion(promo.id)}
										aria-label="Remove promotion"
									>
										×
									</button>
								)}
							</div>
						</div>
					))}

					{/* Free Resins */}
					{selectedFreeResins.map((resin) => (
						<div key={resin.uniqueId || resin.resinId} className="sprintry-cart-summary__item sprintry-cart-summary__item--free">
							<div className="sprintry-cart-summary__item-details">
								<div className="sprintry-cart-summary__item-name">
									{resin.name}
									<span className="sprintry-cart-summary__item-badge sprintry-cart-summary__item-badge--free">Gratis</span>
								</div>
								<div className="sprintry-cart-summary__item-meta">
									<span className="sprintry-cart-summary__item-quantity">1 ×</span>
									<span className="sprintry-cart-summary__item-price">
										{formatPrice(0, currency)}
									</span>
								</div>
							</div>
							<div className="sprintry-cart-summary__item-total">
								<span className="sprintry-cart-summary__item-amount">
									{formatPrice(0, currency)}
								</span>
								{onRemoveFreeResin && resin.uniqueId && (
									<button
										type="button"
										className="sprintry-cart-summary__item-remove"
										onClick={() => onRemoveFreeResin(resin.uniqueId!)}
										aria-label="Remove free resin"
									>
										×
									</button>
								)}
							</div>
						</div>
					))}

					{/* Selected Package */}
					{selectedPackage && (
						<div className="sprintry-cart-summary__item sprintry-cart-summary__item--free">
							<div className="sprintry-cart-summary__item-details">
								<div className="sprintry-cart-summary__item-name">
									{selectedPackage.name}
									<span className="sprintry-cart-summary__item-badge sprintry-cart-summary__item-badge--free">Package</span>
								</div>
								<div className="sprintry-cart-summary__item-meta">
									<span className="sprintry-cart-summary__item-quantity">1 ×</span>
									<span className="sprintry-cart-summary__item-price">
										{formatPrice(selectedPackage.value, currency)}
									</span>
								</div>
							</div>
							<div className="sprintry-cart-summary__item-total">
								<span className="sprintry-cart-summary__item-amount">
									{formatPrice(selectedPackage.value, currency)}
								</span>
								{onRemovePackage && (
									<button
										type="button"
										className="sprintry-cart-summary__item-remove"
										onClick={() => onRemovePackage()}
										aria-label="Remove package"
									>
										×
									</button>
								)}
							</div>
						</div>
					)}
				</div>
			)}
		</div>
	);
};
